import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

interface PageMetaProps {
  title?: string;
  description?: string;
  ogImage?: string;
  ogType?: string;
  canonicalPath?: string;
  jsonLd?: object | object[];
  noindex?: boolean;
}

const BASE_URL = "https://dutchdryice.com";

const PageMeta = ({ title, description, ogImage, ogType = "website", canonicalPath, jsonLd, noindex }: PageMetaProps) => {
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    if (title) {
      document.title = `${title} | Dutch DryIce`;
    }

    const setMeta = (property: string, content: string, isOg = false) => {
      const selector = isOg ? `meta[property="${property}"]` : `meta[name="${property}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement("meta");
        if (isOg) meta.setAttribute("property", property);
        else meta.name = property;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, true);
      setMeta("twitter:description", description);
    }

    if (title) {
      const fullTitle = `${title} | Dutch DryIce`;
      setMeta("og:title", fullTitle, true);
      setMeta("twitter:title", fullTitle);
    }


    // Robots noindex (e.g. for transactional pages)
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (noindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.name = "robots";
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = "noindex, nofollow";
    } else if (robotsMeta && robotsMeta.content === "noindex, nofollow") {
      robotsMeta.remove();
    }

    // OG type
    setMeta("og:type", ogType, true);

    // OG image
    if (ogImage) {
      setMeta("og:image", ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`, true);
      setMeta("twitter:image", ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`);
      setMeta("twitter:card", "summary_large_image");
    }

    // Canonical URL: each language URL is self-canonical so hreflang pairs validate.
    // Previously we always stripped /en which made every EN page point to a NL URL,
    // which Ahrefs flagged as "non-canonical in sitemap" and "hreflang to non-canonical".
    const path = canonicalPath || location.pathname;
    const canonicalUrl = `${BASE_URL}${path === "/" ? "" : path}`;

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    setMeta("og:url", canonicalUrl, true);

    // Site name
    setMeta("og:site_name", "Dutch DryIce", true);

    // JSON-LD
    // Remove any pre-existing managed JSON-LD scripts to prevent duplicate schema entries
    // (e.g. duplicate FAQPage flagged by Google Search Console).
    document
      .querySelectorAll('script[type="application/ld+json"][data-managed="page-meta"]')
      .forEach((el) => el.remove());

    const jsonLdElements: HTMLScriptElement[] = [];
    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-managed", "page-meta");
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
        jsonLdElements.push(script);
      });
    }

    return () => {
      document.title = "Dutch DryIce | Droogijs Bestellen";
      jsonLdElements.forEach((el) => el.remove());
    };
  }, [title, description, ogImage, ogType, canonicalPath, jsonLd, noindex, location.pathname, language]);

  return null;
};

export default PageMeta;
